import React, { useEffect, useMemo, useRef, useState } from "react";
import { Volume2, VolumeX, Info, ChevronUp, MapPin, Image as ImageIcon } from "lucide-react";
import { ADVENTURES, Adventure, AdventureMedia } from "../../lib/constants";
import { toDirectMediaUrl } from "../../lib/media";

// Simplified GoPro-style four-square mark
const GoProMark: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <g fill="currentColor">
      <rect x="1" y="1" width="9.5" height="9.5" rx="2.5" />
      <rect x="13.5" y="1" width="9.5" height="9.5" rx="2.5" />
      <rect x="1" y="13.5" width="9.5" height="9.5" rx="2.5" />
      <rect x="13.5" y="13.5" width="9.5" height="9.5" rx="2.5" />
    </g>
  </svg>
);

const formatTimecode = (seconds: number) => {
  const total = Math.max(0, Math.floor(seconds));
  const mm = String(Math.floor(total / 60)).padStart(2, "0");
  const ss = String(total % 60).padStart(2, "0");
  return `00:${mm}:${ss}`;
};

/** One screen of the feed: a single photo or clip, plus its adventure's metadata. */
type Clip = {
  key: string;
  adventure: Adventure;
  media: AdventureMedia;
  position: number;
  total: number;
};

interface SlideProps {
  clip: Clip;
  index: number;
  isActive: boolean;
  /** Within one slide of the viewport — only these mount their media. */
  isNear: boolean;
  muted: boolean;
  showStats: boolean;
  registerRef: (el: HTMLDivElement | null, index: number) => void;
}

const Slide: React.FC<SlideProps> = ({
  clip,
  index,
  isActive,
  isNear,
  muted,
  showStats,
  registerRef,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mediaFailed, setMediaFailed] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const { adventure, media } = clip;
  const src = useMemo(() => toDirectMediaUrl(media.src), [media.src]);
  const poster = useMemo(
    () => (media.poster ? toDirectMediaUrl(media.poster) : undefined),
    [media.poster]
  );

  const isVideo = media.type === "video" && !mediaFailed;
  const isImage = media.type === "image" && !mediaFailed;
  const isYoutube = media.type === "youtube" && !mediaFailed;
  const caption = media.caption || adventure.caption;

  // Only the slide in view plays; everything else rewinds and pauses so you
  // never have two clips talking over each other. `isNear` is in the deps
  // because the element doesn't exist until the slide comes within range.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isActive) {
      const played = video.play();
      if (played) played.catch(() => {});
    } else {
      video.pause();
      video.currentTime = 0;
      setElapsed(0);
    }
  }, [isActive, isNear, isVideo]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  return (
    <div
      ref={(el) => registerRef(el, index)}
      data-index={index}
      className="relative h-full w-full shrink-0 snap-start snap-always overflow-hidden bg-black"
    >
      {/* Placeholder sits under the media at all times, so a slide is never a
          black rectangle — it holds the adventure's colour while the file
          streams in, and stays put if it never arrives. */}
      <div
        className="absolute inset-0 flex flex-col items-center justify-center pb-40"
        style={{
          background: `radial-gradient(circle at 50% 35%, ${adventure.accent}55 0%, #0b0b0b 70%)`,
        }}
      >
        <div className="text-7xl mb-4 drop-shadow-lg">{adventure.emoji}</div>

        {mediaFailed ? (
          <div className="text-white/40 text-[11px] font-mono px-8 text-center break-all">
            {media.type} unavailable — add it at
            <br />
            <span className="text-white/60">{media.src}</span>
          </div>
        ) : (
          <div
            className={`h-5 w-5 rounded-full border-2 border-white/20 border-t-white/70 transition-opacity duration-300 ${
              isNear && !isLoaded ? "opacity-100 animate-spin" : "opacity-0"
            }`}
          />
        )}
      </div>

      {/* Media layer. Only slides within one step of the viewport mount their
          media — otherwise every clip in the feed streams at once. Each fades
          in once it can actually paint, instead of popping from black. */}
      {isNear && isVideo && (
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${
            isLoaded ? "opacity-100" : "opacity-0"
          }`}
          loop
          muted={muted}
          playsInline
          preload="metadata"
          onLoadedData={() => setIsLoaded(true)}
          onTimeUpdate={(e) => {
            // The HUD only shows whole seconds, so bail out of the ~4/sec
            // re-render this would otherwise cause.
            const seconds = Math.floor(e.currentTarget.currentTime);
            setElapsed((prev) => (prev === seconds ? prev : seconds));
          }}
          onError={() => setMediaFailed(true)}
        />
      )}

      {isNear && isImage && (
        <img
          src={src}
          alt={caption}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${
            isLoaded ? "opacity-100" : "opacity-0"
          }`}
          decoding="async"
          onLoad={() => setIsLoaded(true)}
          onError={() => setMediaFailed(true)}
        />
      )}

      {isNear && isYoutube && (
        <iframe
          src={src}
          title={`${adventure.title} — ${clip.position} of ${clip.total}`}
          className="absolute inset-0 h-full w-full"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          onLoad={() => setIsLoaded(true)}
        />
      )}

      {/* Legibility gradients */}
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/70 to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none" />

      {/* Camera HUD — top left. Photos get a frame counter, video gets a
          timecode. Keyed off the declared type so a missing file still reads
          correctly rather than falling back to "REC". */}
      <div className="absolute top-3 left-4 flex items-center gap-2 text-white/90 pointer-events-none">
        {media.type === "image" ? (
          <span className="flex items-center gap-1.5 text-[11px] font-mono tracking-wider">
            <ImageIcon className="w-3 h-3" />
            PHOTO
          </span>
        ) : (
          <span className="flex items-center gap-1.5 text-[11px] font-mono tracking-wider">
            <span
              className="w-2 h-2 rounded-full bg-red-500"
              style={{ animation: "blink 1.6s infinite" }}
            />
            REC
          </span>
        )}
        <span className="text-[11px] font-mono text-white/70">
          {media.type === "image"
            ? `${clip.position}/${clip.total}`
            : formatTimecode(elapsed)}
        </span>
      </div>

      {/* Caption block */}
      <div className="absolute bottom-0 left-0 right-14 p-5 pb-6">
        <div className="flex items-center gap-2 mb-2">
          <span
            className="text-[10px] font-bold tracking-widest px-2 py-0.5 rounded"
            style={{ backgroundColor: adventure.accent, color: "#0b0b0b" }}
          >
            {adventure.title.toUpperCase()}
          </span>
          <span className="text-[11px] text-white/60 font-mono">
            {adventure.date}
          </span>
          {clip.total > 1 && (
            <span className="text-[11px] text-white/40 font-mono">
              · {clip.position}/{clip.total}
            </span>
          )}
        </div>

        <p className="text-white text-[15px] leading-snug font-medium mb-2">
          {caption}
        </p>

        <div className="flex items-center text-white/70 text-xs mb-3">
          <MapPin className="w-3.5 h-3.5 mr-1 shrink-0" />
          <span className="truncate">{adventure.location}</span>
        </div>

        {showStats && (
          <div className="flex gap-2">
            {adventure.stats.map((stat) => (
              <div
                key={stat.label}
                className="flex-1 rounded-lg bg-white/10 backdrop-blur-md border border-white/15 px-2 py-1.5"
              >
                <div className="text-[9px] tracking-widest text-white/50 font-mono">
                  {stat.label}
                </div>
                <div className="text-white font-bold text-sm leading-tight">
                  {stat.value}
                  <span className="text-[10px] font-normal text-white/60">
                    {stat.unit}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const GoProApp: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const [showStats, setShowStats] = useState(true);
  const [hintDismissed, setHintDismissed] = useState(false);

  // Every photo and clip gets its own screen, keeping its adventure's context.
  const clips = useMemo<Clip[]>(
    () =>
      ADVENTURES.flatMap((adventure) =>
        adventure.media.map((media, i) => ({
          key: `${adventure.id}-${i}`,
          adventure,
          media,
          position: i + 1,
          total: adventure.media.length,
        }))
      ),
    []
  );

  const activeClip = clips[activeIndex];

  const registerRef = (el: HTMLDivElement | null, index: number) => {
    slideRefs.current[index] = el;
  };

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio >= 0.6) {
            const index = Number((entry.target as HTMLElement).dataset.index);
            setActiveIndex(index);
            if (index > 0) setHintDismissed(true);
          }
        });
      },
      { root, threshold: [0.6] }
    );

    slideRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [clips.length]);

  return (
    <div className="relative h-full w-full bg-black">
      <div
        ref={containerRef}
        className="h-full w-full overflow-y-auto snap-y snap-mandatory hide-scrollbar"
      >
        {clips.map((clip, index) => (
          <Slide
            key={clip.key}
            clip={clip}
            index={index}
            isActive={index === activeIndex}
            isNear={Math.abs(index - activeIndex) <= 1}
            muted={muted}
            showStats={showStats}
            registerRef={registerRef}
          />
        ))}
      </div>

      {/* Camera badge — the app header already says "GoPro", so this carries the
          capture mode of whatever is on screen instead of repeating the name. */}
      <div className="absolute top-3 right-4 flex items-center gap-1.5 text-white pointer-events-none">
        <GoProMark />
        <span className="text-[11px] font-mono tracking-wider text-white/80">
          {activeClip && activeClip.media.type === "image"
            ? "HERO12 · 27MP"
            : "HERO12 · 4K60"}
        </span>
      </div>

      {/* Action rail. The swipe hint lives here rather than floating over the
          media, so it can't land on the caption when the frame is short. */}
      <div className="absolute right-3 bottom-24 flex flex-col items-center gap-4">
        {!hintDismissed && clips.length > 1 && (
          <div className="flex flex-col items-center text-white/60 pointer-events-none">
            <ChevronUp className="w-4 h-4 animate-bounce" />
            <span className="text-[9px] tracking-widest font-mono">NEXT</span>
          </div>
        )}

        <button
          onClick={() => setMuted((prev) => !prev)}
          aria-label={muted ? "Unmute" : "Mute"}
          className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-black/60 transition-colors"
        >
          {muted ? (
            <VolumeX className="w-5 h-5" />
          ) : (
            <Volume2 className="w-5 h-5" />
          )}
        </button>

        <button
          onClick={() => setShowStats((prev) => !prev)}
          aria-label="Toggle stats"
          className={`w-10 h-10 rounded-full backdrop-blur-md border flex items-center justify-center transition-colors ${
            showStats
              ? "bg-white text-black border-white"
              : "bg-black/40 text-white border-white/20 hover:bg-black/60"
          }`}
        >
          <Info className="w-5 h-5" />
        </button>
      </div>

      {/* Clip position indicator. Left edge, because the right side is taken by
          the action rail and the two collide on short frames. */}
      <div className="absolute left-2 top-1/2 -translate-y-1/2 flex flex-col gap-1.5 pointer-events-none">
        {clips.map((clip, index) => (
          <span
            key={clip.key}
            className={`w-1 rounded-full transition-all duration-300 ${
              index === activeIndex ? "h-5 bg-white" : "h-1.5 bg-white/30"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default GoProApp;
